import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';
import { users as usersApi } from '../api/client.js';
import UserRow from './users/UserRow.jsx';
import UserDetailPanel from './users/UserDetailPanel.jsx';
import InviteModal from './users/InviteModal.jsx';
import EditUserModal from './users/EditUserModal.jsx';
import ResetPasswordModal from './users/ResetPasswordModal.jsx';

export default function UserManagement({ onClose }) {
  const { theme, isDark } = useTheme();
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  // State
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Modal state
  const [showInvite, setShowInvite] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);

  // Activity log
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);

  // Status messages
  const [statusMessage, setStatusMessage] = useState('');

  // Fetch users
  const fetchUsers = useCallback(() => {
    setLoading(true);
    usersApi.list(isAdmin && showInactive ? { all: 'true' } : {})
      .then(data => setUsers(data.users || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [isAdmin, showInactive]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Fetch user detail
  useEffect(() => {
    if (!selectedUserId) {
      setSelectedUserDetail(null);
      return;
    }
    setDetailLoading(true);
    usersApi.get(selectedUserId)
      .then(data => setSelectedUserDetail(data))
      .catch(console.error)
      .finally(() => setDetailLoading(false));
  }, [selectedUserId]);

  // Fetch user activity
  useEffect(() => {
    if (!selectedUserId || activeTab !== 'activity') return;
    setActivityLoading(true);
    usersApi.activity(selectedUserId)
      .then(data => setActivityLog(data.activities || []))
      .catch(console.error)
      .finally(() => setActivityLoading(false));
  }, [selectedUserId, activeTab]);

  const showStatus = (msg) => {
    setStatusMessage(msg);
    setTimeout(() => setStatusMessage(''), 3000);
  };

  // Handlers
  const handleInvite = async ({ email, name, role }) => {
    await usersApi.invite({ email, name, role });
    showStatus(`Invited ${email}`);
    fetchUsers();
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await usersApi.updateRole(userId, newRole);
      showStatus('Role updated');
      fetchUsers();
      if (selectedUserId === userId) {
        usersApi.get(userId).then(setSelectedUserDetail).catch(console.error);
      }
    } catch (err) {
      alert(err.data?.error || 'Failed to update role');
    }
  };

  const handleDeactivate = async (userId, userName) => {
    if (!confirm(`Deactivate ${userName}? They will no longer be able to log in.`)) return;
    try {
      await usersApi.deactivate(userId);
      showStatus(`${userName} deactivated`);
      fetchUsers();
      if (selectedUserId === userId) setSelectedUserId(null);
    } catch (err) {
      alert(err.data?.error || 'Failed to deactivate user');
    }
  };

  const handleReactivate = async (userId, userName) => {
    try {
      await usersApi.reactivate(userId);
      showStatus(`${userName} reactivated`);
      fetchUsers();
    } catch (err) {
      alert(err.data?.error || 'Failed to reactivate user');
    }
  };

  const handleEditSave = async (userId, { name, initials, color }) => {
    await usersApi.update(userId, { name, initials, color });
    showStatus('User updated');
    fetchUsers();
    if (selectedUserId === userId) {
      usersApi.get(userId).then(setSelectedUserDetail).catch(console.error);
    }
  };

  const handleResetPassword = async (userId, password) => {
    await usersApi.resetPassword(userId, password);
    showStatus('Password reset');
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    }
    return true;
  });

  const activeUsers = filteredUsers.filter(u => u.is_active);
  const inactiveUsers = filteredUsers.filter(u => !u.is_active);
  const selectedDetail = selectedUserDetail?.user;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      background: theme.overlay, backdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '92%', maxWidth: 1100, maxHeight: '90vh',
          background: isDark ? '#141418' : '#FFFFFF',
          borderRadius: 16,
          border: `1px solid ${theme.border}`, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${theme.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: theme.text }}>
              Team Management
            </h2>
            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>
              {activeUsers.length} active member{activeUsers.length !== 1 ? 's' : ''}
              {inactiveUsers.length > 0 && ` · ${inactiveUsers.length} inactive`}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {statusMessage && (
              <span style={{
                fontSize: 12, color: theme.green, fontWeight: 500,
                padding: '4px 12px', background: theme.greenBg,
                borderRadius: 6, border: `1px solid ${theme.greenBorder}`,
              }}>{statusMessage}</span>
            )}
            {isAdmin && (
              <button
                onClick={() => setShowInvite(true)}
                style={{
                  padding: '8px 16px', fontSize: 12, fontWeight: 600,
                  background: 'linear-gradient(135deg, #4F8EF7, #7C5CFC)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: '0 2px 8px rgba(124,92,252,0.25)',
                }}
              >+ Invite User</button>
            )}
            {isAdmin && (
              <button
                onClick={() => setShowInactive(!showInactive)}
                style={{
                  padding: '8px 12px', fontSize: 11, fontWeight: 500,
                  background: showInactive ? theme.accentBg : theme.bgInput,
                  border: `1px solid ${showInactive ? theme.accentBorder : theme.borderMedium}`,
                  borderRadius: 8, color: showInactive ? theme.accent : theme.textMuted,
                  cursor: 'pointer', fontFamily: 'inherit',
                }}
              >{showInactive ? 'Hide' : 'Show'} Inactive</button>
            )}
            <button
              onClick={onClose}
              style={{
                background: theme.bgInput, border: `1px solid ${theme.borderMedium}`,
                borderRadius: 8, color: theme.textMuted,
                padding: '8px 12px', cursor: 'pointer', fontSize: 14,
              }}
            >&#x2715;</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* User List */}
          <div style={{
            width: selectedUserId ? '45%' : '100%',
            borderRight: selectedUserId ? `1px solid ${theme.border}` : 'none',
            display: 'flex', flexDirection: 'column',
            transition: 'width 0.2s',
          }}>
            {/* Search */}
            <div style={{ padding: '12px 16px', borderBottom: `1px solid ${theme.borderLight}` }}>
              <div style={{
                display: 'flex', alignItems: 'center',
                background: theme.bgInput, borderRadius: 8, padding: '0 12px',
                border: `1px solid ${theme.border}`,
              }}>
                <span style={{ color: theme.textFaint, fontSize: 14, marginRight: 8 }}>&#x2315;</span>
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  style={{
                    background: 'none', border: 'none', color: theme.text,
                    fontSize: 12, padding: '8px 0', width: '100%', outline: 'none',
                    fontFamily: 'inherit',
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{
                      background: 'none', border: 'none', color: theme.textFaint,
                      cursor: 'pointer', fontSize: 14, padding: '0 4px',
                    }}
                  >&#x2715;</button>
                )}
              </div>
            </div>

            {/* Users */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: 40, color: theme.textMuted, fontSize: 12 }}>
                  Loading users...
                </div>
              ) : filteredUsers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 40, color: theme.textFaint }}>
                  <div style={{ fontSize: 24, marginBottom: 8, opacity: 0.5 }}>&#x2603;</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>No users found</div>
                </div>
              ) : (
                <>
                  {activeUsers.map(u => (
                    <UserRow
                      key={u.id}
                      user={u}
                      isSelected={selectedUserId === u.id}
                      isCurrent={currentUser?.id === u.id}
                      isAdmin={isAdmin}
                      theme={theme}
                      onClick={() => setSelectedUserId(selectedUserId === u.id ? null : u.id)}
                      onRoleChange={handleRoleChange}
                      onEdit={(user) => setEditingUser(user)}
                      onDeactivate={() => handleDeactivate(u.id, u.name)}
                    />
                  ))}
                  {showInactive && inactiveUsers.length > 0 && (
                    <>
                      <div style={{
                        padding: '12px 20px 6px',
                        fontSize: 10, fontWeight: 600, color: theme.textFaint,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>Inactive Users</div>
                      {inactiveUsers.map(u => (
                        <UserRow
                          key={u.id}
                          user={u}
                          isSelected={selectedUserId === u.id}
                          isCurrent={false}
                          isAdmin={isAdmin}
                          theme={theme}
                          isInactive
                          onClick={() => setSelectedUserId(selectedUserId === u.id ? null : u.id)}
                          onReactivate={() => handleReactivate(u.id, u.name)}
                        />
                      ))}
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Detail Panel */}
          {selectedUserId && (
            <div style={{
              width: '55%', display: 'flex', flexDirection: 'column',
              overflow: 'hidden',
            }}>
              {detailLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: theme.textMuted, fontSize: 12 }}>
                  Loading...
                </div>
              ) : (
                <UserDetailPanel
                  selectedDetail={selectedDetail}
                  currentUser={currentUser}
                  isAdmin={isAdmin}
                  theme={theme}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  activityLog={activityLog}
                  activityLoading={activityLoading}
                  onRoleChange={handleRoleChange}
                  onDeactivate={handleDeactivate}
                  onReactivate={handleReactivate}
                  onEdit={(user) => setEditingUser(user)}
                  onResetPassword={(userId) => setResetPasswordUserId(userId)}
                />
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvite={handleInvite}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleEditSave}
        />
      )}

      {resetPasswordUserId && (
        <ResetPasswordModal
          userId={resetPasswordUserId}
          onClose={() => setResetPasswordUserId(null)}
          onReset={handleResetPassword}
        />
      )}
    </div>
  );
}
